import { z } from "zod";
import { BaseHttpService, validateWithSchema } from "@/api/http/BaseHttpService";
import {
  chatSessionBaseSchema,
  chatSessionSchema,
  type ChatSession,
  type ChatSessionBase,
  type CreateChatSessionPayload,
  type IndexChatSessionsParams,
  type PromptChatSessionPayload,
} from "@/api/http/schemas/chatSessionSchemas";

export const chatEndpoints = {
  index: "borrower_platform/ai_agent_sessions",
  single: "borrower_platform/ai_agent_sessions/:id",
  prompt: "borrower_platform/ai_agent_sessions/:id/prompt",
  finish: "borrower_platform/ai_agent_sessions/:id/finish",
  files: "borrower_platform/ai_agent_sessions/:id/files",
} as const;

export class ChatSessionHttpService extends BaseHttpService {
  screenLoadingPaths = [];

  async create(payload: CreateChatSessionPayload) {
    const { data } = await this.post<{ data: ChatSessionBase }>(chatEndpoints.index, payload);

    return validateWithSchema(chatSessionBaseSchema, data);
  }

  async show(sessionId: ChatSession["id"]) {
    const url = chatEndpoints.single.replace(":id", String(sessionId));

    const { data } = await this.get<{ data: ChatSession }>(url);

    return validateWithSchema(chatSessionSchema, data);
  }

  async showAll(params: IndexChatSessionsParams) {
    const { data } = await this.get<{ data: ChatSessionBase[] }>(chatEndpoints.index, {
      params,
    });

    return validateWithSchema(z.array(chatSessionBaseSchema), data);
  }

  async prompt(id: ChatSession["id"], payload: PromptChatSessionPayload) {
    const url = chatEndpoints.prompt.replace(":id", String(id));

    await this.post(url, payload);
  }

  async finish(id: ChatSession["id"]) {
    const url = chatEndpoints.finish.replace(":id", String(id));

    await this.put(url);
  }

  async uploadFile(id: ChatSession["id"], file: File): Promise<{ file_id: number }> {
    const url = chatEndpoints.files.replace(":id", String(id));
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.post<{ data: { id: number } }>(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return { file_id: response.data.id };
  }
}
